'use client';

import { useParams, useRouter } from 'next/navigation';
import { useCallback, useMemo } from 'react';
import {
  getStepBySlug,
  getVisibleStepIndex,
  getVisibleSteps,
  STEPS,
} from '../config/steps';
import type { StepConfig, ValidationResult } from '../types';
import { useWizardContext } from './useWizardContext';

interface UseWizardNavigationResult {
  /** Current step configuration based on URL */
  currentStep: StepConfig | undefined;
  /** Index of current step in visible steps array */
  currentStepIndex: number;
  /** Array of visible steps based on current state */
  visibleSteps: StepConfig[];
  /** Whether there is a next step */
  hasNextStep: boolean;
  /** Whether there is a previous step */
  hasPreviousStep: boolean;
  /** Navigate to the next step */
  goToNextStep: () => void;
  /** Navigate to the previous step */
  goToPreviousStep: () => void;
  /** Navigate to a specific step by slug */
  goToStep: (slug: string) => void;
  /** Navigate to the quote page */
  goToQuote: () => void;
  /** Validate current step and complete it if valid */
  validateAndCompleteStep: () => ValidationResult;
  /** Check if a step has been completed */
  isStepCompleted: (stepId: string) => boolean;
  /** Check if a step can be navigated to (all previous steps completed) */
  canNavigateToStep: (slug: string) => boolean;
}

/**
 * Hook for managing wizard navigation.
 *
 * Provides navigation functions, step visibility logic, and validation.
 * Must be used within a WizardProvider.
 */
export function useWizardNavigation(): UseWizardNavigationResult {
  const { state, dispatch } = useWizardContext();
  const router = useRouter();
  const params = useParams();
  const stepSlug = params.step as string | undefined;

  // Get visible steps based on current state
  const visibleSteps = useMemo(() => getVisibleSteps(state), [state]);

  const allSteps = useMemo(() => STEPS, []);

  // Get current step configuration
  const currentStep = useMemo(() => {
    if (!stepSlug) return undefined;
    return getStepBySlug(stepSlug);
  }, [stepSlug]);

  // Get current step index in visible steps
  const currentStepIndex = useMemo(() => {
    if (!stepSlug) return -1;
    return getVisibleStepIndex(stepSlug, state);
  }, [stepSlug, state]);

  // Check if there's a next step
  const hasNextStep = useMemo(() => {
    return currentStepIndex >= 0 && currentStepIndex < visibleSteps.length - 1;
  }, [currentStepIndex, visibleSteps.length]);

  // Check if there's a previous step
  const hasPreviousStep = useMemo(() => {
    return currentStepIndex > 0;
  }, [currentStepIndex]);

  // Check if a step is completed
  const isStepCompleted = useCallback(
    (stepId: string) => {
      return state.completedSteps.includes(stepId);
    },
    [state.completedSteps]
  );

  // Check if we can navigate to a specific step
  const canNavigateToStep = useCallback(
    (slug: string) => {
      const targetIndex = getVisibleStepIndex(slug, state);
      if (targetIndex < 0) return false;

      // Can always go back to completed steps
      // Can only go forward if all previous steps are completed
      for (let i = 0; i < targetIndex; i++) {
        const step = visibleSteps[i];
        if (!state.completedSteps.includes(step.id)) {
          return false;
        }
      }

      return true;
    },
    [state, visibleSteps]
  );

  // Validate and complete the current step
  const validateAndCompleteStep = useCallback((): ValidationResult => {
    if (!currentStep) {
      return { isValid: false, errors: { step: 'No current step' } };
    }

    const result = currentStep.validate(state);

    if (result.isValid) {
      const currentIndex = allSteps.indexOf(currentStep);

      dispatch({ type: 'COMPLETE_STEP', payload: currentStep.id });

      for (let i = currentIndex + 1; i < allSteps.length; i++) {
        const next = allSteps[i];

        if (
          !next.parent &&
          next.id !== 'pediatrics-upsell' &&
          !next.isVisible(state)
        ) {
          dispatch({ type: 'COMPLETE_STEP', payload: next.id });
        }
      }
    }

    return result;
  }, [currentStep, state, allSteps, dispatch]);

  // Navigate to next step
  // Note: Step components handle their own validation before calling this.
  // We just mark the step complete and navigate.
  const goToNextStep = useCallback(() => {
    if (currentStep) {
      dispatch({ type: 'COMPLETE_STEP', payload: currentStep.id });

      for (let i = currentStepIndex + 1; i < allSteps.length; i++) {
        const next = allSteps[i];

        if (
          !next.parent &&
          next.id !== 'pediatrics-upsell' &&
          !next.isVisible(state)
        ) {
          dispatch({ type: 'COMPLETE_STEP', payload: next.id });
        }
      }
    }
    dispatch({ type: 'SET_ERROR', payload: null });

    if (hasNextStep) {
      const nextStep = visibleSteps[currentStepIndex + 1];
      router.push(`/intake-v2/${nextStep.slug}`);
    } else {
      // All steps completed, go to quote
      router.push('/intake-v2/quote');
    }
  }, [
    currentStep,
    hasNextStep,
    visibleSteps,
    currentStepIndex,
    router,
    allSteps,
    state,
    dispatch,
  ]);

  // Navigate to previous step
  const goToPreviousStep = useCallback(() => {
    if (!hasPreviousStep) return;

    const prevStep = visibleSteps[currentStepIndex - 1];
    router.push(`/intake-v2/${prevStep.slug}`);
  }, [hasPreviousStep, visibleSteps, currentStepIndex, router]);

  // Navigate to a specific step
  const goToStep = useCallback(
    (slug: string) => {
      if (!canNavigateToStep(slug)) {
        console.warn(`Cannot navigate to step: ${slug}`);
        return;
      }
      router.push(`/intake-v2/${slug}`);
    },
    [canNavigateToStep, router]
  );

  // Navigate to quote page
  const goToQuote = useCallback(() => {
    router.push('/intake-v2/quote');
  }, [router]);

  return {
    currentStep,
    currentStepIndex,
    visibleSteps,
    hasNextStep,
    hasPreviousStep,
    goToNextStep,
    goToPreviousStep,
    goToStep,
    goToQuote,
    validateAndCompleteStep,
    isStepCompleted,
    canNavigateToStep,
  };
}
